import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Snackbar,
} from "@mui/material";
import { Add, Delete, ContentCopy } from "@mui/icons-material";
import apiClient from "../utils/apiClient";

const InviteUsers = () => {
  const [invitations, setInvitations] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("/admin/invitations");
      setInvitations(response.data.invitations);
    } catch (err) {
      setError("Failed to fetch invitations");
      console.error("Failed to fetch invitations:", err);
    } finally {
      setLoading(false);
    }
  };

  const createInvitation = async () => {
    if (!newEmail.trim()) {
      setError("Please enter an email address");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const response = await apiClient.post("/admin/invitations", {
        email: newEmail.trim(),
      });
      
      setSuccess(`Invitation created for ${newEmail}`);
      setNewEmail("");
      fetchInvitations(); // Refresh the list
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create invitation");
      console.error("Failed to create invitation:", err);
    } finally {
      setLoading(false);
    }
  };

  const deleteInvitation = async (codeUuid, email) => {
    if (!window.confirm(`Are you sure you want to delete the invitation for ${email}?`)) {
      return;
    }

    try {
      setLoading(true);
      await apiClient.delete(`/admin/invitations/${codeUuid}`);
      
      setSuccess(`Invitation for ${email} deleted successfully`);
      fetchInvitations(); // Refresh the list
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete invitation");
      console.error("Failed to delete invitation:", err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbarMessage("Invitation code copied to clipboard!");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      setSnackbarMessage("Failed to copy to clipboard");
      setSnackbarOpen(true);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString() + " " + new Date(dateString).toLocaleTimeString();
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        User Invitations
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Create Invitation Form */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Create New Invitation
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            label="Email Address"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="user@example.com"
            sx={{ flexGrow: 1 }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={createInvitation}
            disabled={loading || !newEmail.trim()}
          >
            Create Invitation
          </Button>
        </Box>
      </Paper>

      {/* Invitations List */}
      <Typography variant="h6" gutterBottom>
        Existing Invitations
      </Typography>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Invitation Code</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Used</TableCell>
                <TableCell>Edit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invitations.map((invitation) => (
                <TableRow key={invitation.code_uuid}>
                  <TableCell>{invitation.email}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {invitation.code}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(invitation.code)}
                        title="Copy to clipboard"
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={invitation.used ? "Used" : "Unused"}
                      color={invitation.used ? "default" : "primary"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(invitation.created_at)}</TableCell>
                  <TableCell>
                    {invitation.used_at ? formatDate(invitation.used_at) : "-"}
                  </TableCell>
                  <TableCell>
                    {!invitation.used && (
                      <IconButton
                        size="small"
                        onClick={() => deleteInvitation(invitation.code_uuid, invitation.email)}
                        disabled={loading}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {invitations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No invitations found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default InviteUsers;
